import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '@/components/Sidebar';
import { 
  Sparkles, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  MessageSquare, 
  Zap,
  Send,
  Target,
  Brain,
  Lightbulb,
  Clock,
  ArrowUpRight,
  CheckCircle2,
  XCircle,
  Info,
  RefreshCw,
  ChevronLeft,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// Improved markdown renderer function
const renderMarkdown = (text) => {
  if (!text) return null;
  
  // Process inline formatting (bold, italic)
  const processInlineFormatting = (line) => {
    if (!line) return line;
    
    const parts = [];
    let key = 0;
    
    // Regex to match **bold** or __bold__ anywhere in text
    const boldRegex = /\*\*([^*]+)\*\*|__([^_]+)__/g;
    let lastIndex = 0;
    let match;
    
    const lineStr = String(line);
    
    while ((match = boldRegex.exec(lineStr)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(<span key={key++}>{lineStr.slice(lastIndex, match.index)}</span>);
      }
      // Add the bold text
      const boldText = match[1] || match[2];
      parts.push(
        <strong key={key++} className="font-semibold text-slate-900 dark:text-zinc-50">
          {boldText}
        </strong>
      );
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < lineStr.length) {
      parts.push(<span key={key++}>{lineStr.slice(lastIndex)}</span>);
    }
    
    return parts.length > 0 ? parts : lineStr;
  };
  
  // Split into lines
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let inList = false;
  let listKey = 0;
  
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`ul-${listKey++}`} className="list-disc pl-5 space-y-1 my-2">
          {listItems}
        </ul>
      );
      listItems = [];
    }
    inList = false;
  };
  
  lines.forEach((line, idx) => {
    const trimmedLine = line.trim();
    
    // Skip empty lines but flush list if we were in one
    if (!trimmedLine) {
      if (inList) flushList();
      return;
    }
    
    // Check if it's a numbered list (1. 2. 3. etc)
    const numberedMatch = trimmedLine.match(/^(\d+)\.\s+(.+)$/);
    if (numberedMatch) {
      if (!inList) inList = true;
      listItems.push(
        <li key={`li-${idx}`} className="text-sm leading-relaxed">
          {processInlineFormatting(numberedMatch[2])}
        </li>
      );
      return;
    }
    
    // Check if it's a bullet list item (* or - or •)
    const bulletMatch = trimmedLine.match(/^[\*\-•]\s+(.+)$/);
    if (bulletMatch) {
      if (!inList) inList = true;
      listItems.push(
        <li key={`li-${idx}`} className="text-sm leading-relaxed">
          {processInlineFormatting(bulletMatch[1])}
        </li>
      );
      return;
    }
    
    // Not a list item - flush any pending list
    if (inList) flushList();
    
    // Regular paragraph
    elements.push(
      <p key={`p-${idx}`} className="mb-2 text-sm leading-relaxed">
        {processInlineFormatting(trimmedLine)}
      </p>
    );
  });
  
  // Flush any remaining list items
  if (inList) flushList();
  
  return elements;
};

// ============================================================================
// AI INSIGHTS PAGE - Full Functional Implementation
// ============================================================================

const AIInsightsPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [chatInput, setChatInput] = useState('');
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [askingAI, setAskingAI] = useState(false);
  const [insights, setInsights] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);

  // Fetch AI analysis on mount and period change
  useEffect(() => {
    if (user?.id) {
      fetchAnalysis(false); // Don't force refresh on period change
    }
  }, [user?.id, period]);

  const fetchAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai-insights/analyze`, {
        params: { user_id: user.id, period, refresh: forceRefresh },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setInsights(response.data.analysis);
        setCacheInfo(response.data.cache_info);
        setGeneratedAt(response.data.generated_at);
        
        // Show toast based on cache status
        if (response.data.from_cache) {
          toast.info('Menampilkan hasil analisis tersimpan', { duration: 2000 });
        }
      }
    } catch (error) {
      console.error('Failed to fetch AI analysis:', error);
      toast.error('Gagal memuat analisis AI');
    } finally {
      setLoading(false);
    }
  };

  const handleReanalyze = async () => {
    setAnalyzing(true);
    await fetchAnalysis(true); // Force refresh
    setAnalyzing(false);
    toast.success('Analisis baru selesai!');
  };
  
  // Format time ago
  const formatTimeAgo = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'baru saja';
    if (diffMins < 60) return `${diffMins} menit lalu`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} jam lalu`;
    return date.toLocaleDateString('id-ID');
  };

  const handleAskAI = async () => {
    if (!chatInput.trim()) return;
    
    const question = chatInput.trim();
    setChatInput('');
    
    // Add user message to chat
    setChatHistory(prev => [...prev, { role: 'user', message: question }]);
    setAskingAI(true);
    
    try {
      const response = await axios.post(`${API}/ai-insights/ask`, null, {
        params: { user_id: user.id, question },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setChatHistory(prev => [...prev, { role: 'ai', message: response.data.answer }]);
      }
    } catch (error) {
      console.error('Failed to ask AI:', error);
      setChatHistory(prev => [...prev, { 
        role: 'ai', 
        message: 'Maaf, terjadi kesalahan. Silakan coba lagi.' 
      }]);
    } finally {
      setAskingAI(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'urgent': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
      case 'high': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800';
      case 'medium': return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800';
      default: return 'bg-slate-100 text-slate-700 dark:bg-zinc-900 dark:text-zinc-400';
    }
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'opportunity': return <Lightbulb className="w-5 h-5 text-emerald-500" />;
      case 'alert': return <Clock className="w-5 h-5 text-red-500" />;
      case 'positive': return <CheckCircle2 className="w-5 h-5 text-emerald-500" />;
      default: return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'good': return 'text-emerald-600 dark:text-emerald-400';
      case 'warning': return 'text-amber-600 dark:text-amber-400';
      case 'critical': return 'text-red-600 dark:text-red-400';
      default: return 'text-slate-600 dark:text-zinc-400';
    }
  };

  const quickQuestions = [
    "Produk terlaris?",
    "Kenapa sales turun?",
    "Rekomendasi promo?",
    "Customer terbanyak?"
  ];

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      
      <div className="flex-1 lg:ml-64">
        <div className="p-3 sm:p-4 lg:p-6">
          <div className="max-w-7xl mx-auto space-y-3 sm:space-y-6">
            
            {/* ============ HEADER ============ */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="p-1.5 sm:p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-zinc-900 lg:hidden"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <div className="p-2 sm:p-3 rounded-xl sm:rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 shadow-lg shadow-indigo-500/25">
                  <Brain className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div>
                  <h1 className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">AI Insights</h1>
                  <p className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400">Analisa cerdas untuk bisnis Anda</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Period selector */}
                <div className="flex rounded-lg bg-slate-100 dark:bg-zinc-900 p-0.5 sm:p-1">
                  {['today', 'week', 'month'].map((p) => (
                    <button
                      key={p}
                      onClick={() => setPeriod(p)}
                      className={`px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-md text-xs sm:text-sm font-medium transition-all ${
                        period === p 
                          ? 'bg-white dark:bg-zinc-800 text-slate-900 dark:text-zinc-50 shadow-sm' 
                          : 'text-slate-600 dark:text-zinc-400 hover:text-slate-900 dark:hover:text-white'
                      }`}
                    >
                      {p === 'today' ? 'Hari Ini' : p === 'week' ? 'Minggu Ini' : 'Bulan Ini'}
                    </button>
                  ))}
                </div>
                
                <Button
                  onClick={handleReanalyze}
                  disabled={analyzing}
                  size="sm"
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-indigo-500/25 text-xs sm:text-sm"
                >
                  {analyzing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Analisa Ulang
                </Button>
              </div>
            </div>
            
            {/* Cache Info Banner */}
            {cacheInfo?.cached && generatedAt && (
              <div className="flex items-center justify-between px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-blue-100 dark:border-indigo-800">
                <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-indigo-700 dark:text-indigo-300 flex-wrap">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
                  <span>Diperbarui {formatTimeAgo(generatedAt)}</span>
                  <span className="text-blue-500 dark:text-indigo-400 hidden sm:inline">•</span>
                  <span className="text-[10px] sm:text-xs hidden sm:inline">Otomatis update setiap {cacheInfo.cache_duration_minutes} menit</span>
                </div>
                <button 
                  onClick={handleReanalyze}
                  disabled={analyzing}
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                >
                  {analyzing ? 'Menganalisa...' : 'Refresh sekarang'}
                </button>
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mx-auto mb-4" />
                  <p className="text-slate-600 dark:text-zinc-400">Menganalisis data bisnis Anda...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-6">
                
                {/* ============ LEFT COLUMN - INSIGHTS ============ */}
                <div className="lg:col-span-3 space-y-3 sm:space-y-6">
                  
                  {/* Summary Card */}
                  <div className="relative overflow-hidden rounded-xl sm:rounded-xl bg-white/70 dark:bg-zinc-900/70 backdrop-blur-xl border border-white/20 dark:border-zinc-800/50 p-4 sm:p-6 shadow-sm">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-violet-500/10 to-indigo-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                    
                    <div className="relative">
                      <div className="flex items-start justify-between mb-3 sm:mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-500" />
                            <span className="text-xs sm:text-sm font-medium text-indigo-600 dark:text-indigo-400">AI Summary</span>
                          </div>
                          <h2 className="text-base sm:text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">
                            Analisa Bisnis {period === 'today' ? 'Hari Ini' : period === 'week' ? 'Minggu Ini' : 'Bulan Ini'}
                          </h2>
                        </div>
                        <div className="text-right shrink-0 ml-3">
                          <div className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-zinc-50">
                            {insights?.health_score || 0}
                          </div>
                          <div className={`flex items-center justify-end gap-1 text-xs sm:text-sm ${
                            insights?.health_trend?.startsWith('+') ? 'text-emerald-600' : 'text-amber-600'
                          } dark:text-emerald-400`}>
                            {insights?.health_trend?.startsWith('+') ? (
                              <TrendingUp className="w-4 h-4" />
                            ) : (
                              <TrendingDown className="w-4 h-4" />
                            )}
                            {insights?.health_trend || '0'} dari periode lalu
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-sm sm:text-base text-slate-600 dark:text-zinc-300 leading-relaxed">
                        {insights?.summary || 'Menganalisis data bisnis Anda...'}
                      </p>
                    </div>
                  </div>

                  {/* Quick Metrics */}
                  {insights?.quick_metrics && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
                      {insights.quick_metrics.map((metric, idx) => (
                        <div key={idx} className="rounded-xl bg-white/70 dark:bg-zinc-900/70 backdrop-blur-xl border border-white/20 dark:border-zinc-800/50 p-3 sm:p-4 shadow-sm">
                          <div className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400 mb-0.5 sm:mb-1">{metric.label}</div>
                          <div className="flex items-end gap-1">
                            <span className="text-lg sm:text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-50">{metric.value}</span>
                            <span className="text-xs sm:text-sm text-slate-500 dark:text-zinc-400 mb-0.5">{metric.suffix}</span>
                          </div>
                          <div className={`flex items-center gap-1 text-xs ${getStatusColor(metric.status)}`}>
                            {metric.trend?.startsWith('+') ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {metric.trend}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Critical Findings */}
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm sm:text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 flex items-center gap-2">
                        <Target className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-500" />
                        Temuan & Rekomendasi
                      </h3>
                      <span className="text-[10px] sm:text-xs text-slate-500 dark:text-zinc-400">
                        {insights?.findings?.length || 0} item perlu perhatian
                      </span>
                    </div>
                    
                    {insights?.findings?.map((finding, idx) => (
                      <div key={idx} className="rounded-xl sm:rounded-xl bg-white/70 dark:bg-zinc-900/70 backdrop-blur-xl border border-white/20 dark:border-zinc-800/50 overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
                        <div className="p-3 sm:p-5">
                          <div className="flex items-start gap-2.5 sm:gap-4">
                            <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-slate-100 dark:bg-zinc-800 shrink-0">
                              {getTypeIcon(finding.type)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1.5 sm:mb-2 flex-wrap">
                                <h4 className="font-semibold text-sm sm:text-base text-slate-900 dark:text-zinc-50">{finding.title}</h4>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(finding.priority)}`}>
                                  {finding.priority === 'urgent' ? 'Urgent' : finding.priority === 'high' ? 'High' : 'Medium'}
                                </span>
                              </div>
                              
                              <p className="text-xs sm:text-sm text-slate-600 dark:text-zinc-300 mb-2 sm:mb-3 leading-relaxed">
                                {finding.analysis}
                              </p>
                              
                              <div className="space-y-1.5 sm:space-y-0 sm:flex sm:flex-wrap sm:gap-3 mb-2 sm:mb-4">
                                <div className="flex items-start gap-1 sm:gap-1.5 text-[10px] sm:text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 sm:px-2.5 py-1 sm:py-1 rounded-lg">
                                  <XCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 mt-0.5" />
                                  <span>{finding.impact}</span>
                                </div>
                                <div className="flex items-start gap-1 sm:gap-1.5 text-[10px] sm:text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 sm:px-2.5 py-1 sm:py-1 rounded-lg">
                                  <CheckCircle2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 shrink-0 mt-0.5" />
                                  <span>Estimasi: {finding.estimated_impact}</span>
                                </div>
                              </div>
                              
                              <div className="flex items-center justify-between pt-2 sm:pt-3 border-t border-slate-100 dark:border-zinc-800 flex-wrap gap-2 sm:gap-3">
                                <div className="flex items-center gap-1.5 sm:gap-2">
                                  <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-500 shrink-0" />
                                  <span className="text-xs sm:text-sm font-medium text-slate-700 dark:text-zinc-300">{finding.recommendation}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {(!insights?.findings || insights.findings.length === 0) && (
                      <div className="text-center py-10 text-slate-500 dark:text-zinc-400">
                        <CheckCircle2 className="w-12 h-12 mx-auto mb-3 text-emerald-500" />
                        <p>Tidak ada temuan kritis. Bisnis Anda berjalan dengan baik!</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* ============ RIGHT COLUMN - ASK AI ============ */}
                <div className="lg:col-span-1">
                  <div className="sticky top-6 rounded-xl bg-white/70 dark:bg-zinc-900/70 backdrop-blur-xl border border-white/20 dark:border-zinc-800/50 shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="p-4 border-b border-slate-100 dark:border-zinc-800 bg-gradient-to-r from-violet-600/5 to-indigo-600/5">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600">
                          <MessageSquare className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-sm">Tanya AI</h3>
                          <p className="text-xs text-slate-500 dark:text-zinc-400">Analisa data real-time</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Chat Messages */}
                    <div className="h-48 sm:h-80 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                      {chatHistory.length === 0 ? (
                        <div className="text-center text-slate-500 dark:text-zinc-500 py-4 sm:py-8">
                          <Brain className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-xs sm:text-sm">Tanyakan apapun tentang bisnis Anda!</p>
                        </div>
                      ) : (
                        chatHistory.map((chat, idx) => (
                          <div key={idx} className={`flex ${chat.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[90%] p-3 rounded-xl text-sm ${
                              chat.role === 'user' 
                                ? 'bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-200 rounded-tr-sm' 
                                : 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 text-slate-700 dark:text-zinc-200 rounded-tl-sm'
                            }`}>
                              {chat.role === 'ai' ? (
                                <div className="space-y-2">
                                  <div className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 font-medium mb-2">
                                    <Sparkles className="w-3 h-3" />
                                    Nerava AI
                                  </div>
                                  <div className="text-sm leading-relaxed">
                                    {renderMarkdown(chat.message)}
                                  </div>
                                </div>
                              ) : (
                                chat.message
                              )}
                            </div>
                          </div>
                        ))
                      )}
                      {askingAI && (
                        <div className="flex justify-start">
                          <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-xl rounded-tl-sm p-3">
                            <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Input */}
                    <div className="p-3 sm:p-4 border-t border-slate-100 dark:border-zinc-800">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={chatInput}
                          onChange={(e) => setChatInput(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAskAI()}
                          placeholder="Tanya tentang bisnis Anda..."
                          className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full bg-slate-100 dark:bg-zinc-800 border-0 text-xs sm:text-sm text-slate-700 dark:text-zinc-200 placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />
                        <button 
                          onClick={handleAskAI}
                          disabled={askingAI || !chatInput.trim()}
                          className="p-2.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 text-white hover:shadow-lg hover:shadow-indigo-500/25 transition-all duration-200 disabled:opacity-50"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Quick Questions */}
                      <div className="mt-2 sm:mt-3 flex flex-wrap gap-1.5 sm:gap-2">
                        {quickQuestions.map((q, idx) => (
                          <button 
                            key={idx}
                            onClick={() => {
                              setChatInput(q);
                            }}
                            className="px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full bg-slate-100 dark:bg-zinc-800 text-[10px] sm:text-xs text-slate-600 dark:text-zinc-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                          >
                            {q}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIInsightsPage;
